import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Moon, 
  Sun, 
  Globe, 
  Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from 'next-themes';
import NotificationSettings from '@/components/NotificationSettings';
import { SUPPORTED_LANGUAGES, saveLanguagePreference, loadLanguagePreference, type LanguageCode } from '@/lib/i18n';
import { DeveloperResetOnboarding } from '@/components/dev/DeveloperResetOnboarding';

interface AppPreferences {
  theme: string;
  language: string;
  audioQuality: string;
  autoPlayAudio: boolean;
  showCompletionAnimations: boolean;
  twoFactorEnabled: boolean;
}

export function SettingsTab({ userId }: { userId: string }) {
  const { theme, setTheme } = useTheme();
  const [preferences, setPreferences] = useState<AppPreferences>({
    theme: theme || 'system',
    language: 'en',
    audioQuality: 'high',
    autoPlayAudio: true,
    showCompletionAnimations: true,
    twoFactorEnabled: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, [userId]);

  const loadPreferences = async () => {
    try {
      // Load saved language preference
      const savedLang = loadLanguagePreference();
      
      // For now, use local storage until we add app_preferences column to database
      const stored = localStorage.getItem(`app_preferences_${userId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        setPreferences({
          ...preferences,
          ...parsed,
          theme: theme || 'system',
          language: savedLang
        });
      } else {
        setPreferences({
          ...preferences,
          language: savedLang
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);

      // Save to local storage for now
      localStorage.setItem(`app_preferences_${userId}`, JSON.stringify(preferences));
      
      // Save language preference separately for global access
      saveLanguagePreference(preferences.language as LanguageCode);

      toast.success('Settings saved successfully');
      
      // Reload page if language changed to apply translations
      const stored = localStorage.getItem(`app_preferences_${userId}`);
      if (stored) {
        const old = JSON.parse(stored);
        if (old.language !== preferences.language) {
          setTimeout(() => window.location.reload(), 1000);
        }
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeChange = (newTheme: string) => {
    setTheme(newTheme);
    setPreferences({ ...preferences, theme: newTheme });
  };

  return (
    <div className="space-y-6">
      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            Appearance
          </CardTitle>
          <CardDescription>Customise how Therappi® looks</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="theme">Theme</Label>
            <Select value={preferences.theme} onValueChange={handleThemeChange}>
              <SelectTrigger id="theme">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="system">System</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Completion Animations</Label>
              <p className="text-sm text-muted-foreground">
                Show celebratory animations when completing activities
              </p>
            </div>
            <Switch
              checked={preferences.showCompletionAnimations}
              onCheckedChange={(checked) => 
                setPreferences({ ...preferences, showCompletionAnimations: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Language & Region */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Language & Region
          </CardTitle>
          <CardDescription>Set your language preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="language">Language</Label>
            <Select 
              value={preferences.language} 
              onValueChange={(value) => setPreferences({ ...preferences, language: value })}
            >
              <SelectTrigger id="language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.nativeName} ({lang.name})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Page will reload after saving to apply language changes
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Audio Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Volume2 className="h-5 w-5" />
            Audio Settings
          </CardTitle>
          <CardDescription>Manage audio playback preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="audio-quality">Audio Quality</Label>
            <Select 
              value={preferences.audioQuality} 
              onValueChange={(value) => setPreferences({ ...preferences, audioQuality: value })}
            >
              <SelectTrigger id="audio-quality">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low (Save Data)</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Auto-play Guided Audio</Label>
              <p className="text-sm text-muted-foreground">
                Automatically start audio in guided sessions (when voice features are available)
              </p>
            </div>
            <Switch
              checked={preferences.autoPlayAudio}
              onCheckedChange={(checked) => 
                setPreferences({ ...preferences, autoPlayAudio: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <NotificationSettings />

      {/* Save Button */}
      <Button 
        onClick={savePreferences} 
        disabled={saving}
        className="w-full"
      >
        {saving ? 'Saving...' : 'Save All Settings'}
      </Button>

      {/* Developer Reset Onboarding - only visible in dev mode */}
      <DeveloperResetOnboarding userId={userId} />
    </div>
  );
}
